import React, { useEffect, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, ChevronLeft, ChevronRight, Settings2, Check, ShieldAlert, Info, Share2, AlertCircle, Upload, Clock, FileText, Users } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, apiErrorMessage } from "@/lib/api";

const eur = (n) => `${Number(n || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
const MESES = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
const SRC_LABEL = { saved: "Guardado", carry: "Mes anterior", payslip: "De nómina", none: "Sin datos" };

const disponibleDesde = (year, month) => `${month === 2 ? 28 : 30} de ${MESES[month - 1]}`;

function ShareBadge({ status }) {
  if (status === "compartida")
    return <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-[10px] text-emerald-700"><Share2 className="mr-1 h-3 w-3" />Compartida</Badge>;
  if (status === "requiere_correccion")
    return <Badge variant="outline" className="border-rose-200 bg-rose-50 text-[10px] text-rose-700"><AlertCircle className="mr-1 h-3 w-3" />Corrección</Badge>;
  return <Badge variant="outline" className="border-amber-200 bg-amber-50 text-[10px] text-amber-700"><Clock className="mr-1 h-3 w-3" />Pendiente</Badge>;
}

function ConfigDialog({ open, onClose }) {
  const qc = useQueryClient();
  const cfgQ = useQuery({ queryKey: ["pren-config"], queryFn: async () => (await api.get("/prenomina/config")).data, enabled: open });
  const [rate, setRate] = useState("");
  useEffect(() => { if (cfgQ.data && open) setRate(cfgQ.data.guardia_rate); }, [cfgQ.data, open]);
  const saveMut = useMutation({
    mutationFn: async () => (await api.put("/prenomina/config", { guardia_rate: Number(rate) })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pren-config"] }); qc.invalidateQueries({ queryKey: ["pren-sheet"] }); toast.success("Tarifa de guardia actualizada"); onClose(); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="pren-config-dialog">
        <DialogHeader><DialogTitle>Configuración de prenómina</DialogTitle>
          <DialogDescription>Ajusta el importe que se paga por cada guardia realizada.</DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label className="text-xs">Importe por guardia (€)</Label>
          <Input type="number" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="pren-cfg-guardia-rate" />
          <p className="text-[11px] text-muted-foreground">Se multiplica por el nº de guardias de cada empleado. El calendario de guardias se cruzará más adelante.</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending || rate === ""} data-testid="pren-cfg-save">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MembersDialog({ open, onClose }) {
  const qc = useQueryClient();
  const membersQ = useQuery({ queryKey: ["pren-members"], queryFn: async () => (await api.get("/prenomina/members")).data, enabled: open });
  const members = membersQ.data?.members || [];
  const toggleMut = useMutation({
    mutationFn: async ({ user_id, included }) => (await api.post("/prenomina/members", { user_id, included })).data,
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["pren-members"] });
      qc.invalidateQueries({ queryKey: ["pren-sheet"] });
      toast.success(v.included ? "Empleado añadido a la prenómina" : "Empleado excluido de la prenómina");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const incluidos = members.filter((m) => m.included).length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="pren-members-dialog">
        <DialogHeader>
          <DialogTitle>Empleados en la prenómina</DialogTitle>
          <DialogDescription>Activa o desactiva quién aparece en la hoja mensual. Los excluidos no se calculan ni se muestran.</DialogDescription>
        </DialogHeader>
        {membersQ.isLoading ? <Skeleton className="h-64 w-full" /> : (
          <div className="max-h-[55vh] space-y-1 overflow-y-auto pr-1">
            {members.map((m) => (
              <div key={m.user_id} className="flex items-center justify-between gap-3 rounded-lg border px-3 py-2" data-testid={`pren-member-${m.user_id}`}>
                <span className={`text-sm font-medium ${m.included ? "" : "text-muted-foreground line-through"}`}>{m.name}</span>
                <Switch checked={m.included} disabled={toggleMut.isPending} onCheckedChange={(v) => toggleMut.mutate({ user_id: m.user_id, included: v })} data-testid={`pren-member-switch-${m.user_id}`} />
              </div>
            ))}
          </div>
        )}
        <DialogFooter className="items-center justify-between sm:justify-between">
          <span className="text-xs text-muted-foreground">{incluidos} de {members.length} incluidos</span>
          <Button onClick={onClose} data-testid="pren-members-close">Hecho</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function EmployeeRow({ row, year, month }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    nivel_profesional: row.nivel_profesional ?? "",
    bruto_base: row.bruto_base ?? 0,
    num_guardias: row.num_guardias ?? 0,
    plus_productividad: row.plus_productividad ?? 0,
  });
  useEffect(() => {
    setForm({
      nivel_profesional: row.nivel_profesional ?? "",
      bruto_base: row.bruto_base ?? 0,
      num_guardias: row.num_guardias ?? 0,
      plus_productividad: row.plus_productividad ?? 0,
    });
  }, [row.user_id, row.nivel_profesional, row.bruto_base, row.num_guardias, row.plus_productividad]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const dirty =
    (form.nivel_profesional || "") !== (row.nivel_profesional || "") ||
    Number(form.bruto_base || 0) !== Number(row.bruto_base || 0) ||
    Number(form.num_guardias || 0) !== Number(row.num_guardias || 0) ||
    Number(form.plus_productividad || 0) !== Number(row.plus_productividad || 0);

  const rate = row.guardia_rate || 0;
  const guardiasImp = Number(form.num_guardias || 0) * rate;
  const totalPrev = Number(form.bruto_base || 0) + Number(row.comisiones || 0) + guardiasImp + Number(form.plus_productividad || 0);
  const dif = row.real && row.real.bruto != null ? totalPrev - Number(row.real.bruto || 0) : null;
  const [bkOpen, setBkOpen] = useState(false);
  const real = row.real;
  const bkRows = real ? [
    { c: "Bruto base", prev: Number(form.bruto_base || 0), real: Number(real.bruto_base || 0) },
    ...(row.is_sales_team ? [{ c: "Comisiones (16→15)", prev: Number(row.comisiones || 0), real: Number(real.comisiones || 0) }] : []),
    { c: "Guardias", prev: guardiasImp, real: Number(real.guardias || 0) },
    { c: "Plus productividad", prev: Number(form.plus_productividad || 0), real: Number(real.plus_productividad || 0) },
    { c: "Otros (solo en nómina real)", prev: 0, real: Number(real.otros || 0) },
  ].map((x) => ({ ...x, dif: Number((x.prev - x.real).toFixed(2)) })) : [];

  const saveMut = useMutation({
    mutationFn: async () => (await api.put("/prenomina/sheet", {
      user_id: row.user_id, year, month,
      nivel_profesional: form.nivel_profesional || "",
      bruto_base: Number(form.bruto_base || 0),
      num_guardias: Number(form.num_guardias || 0),
      plus_productividad: Number(form.plus_productividad || 0),
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["pren-sheet"] }); toast.success(`Guardado · ${row.name}`); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const shareStatus = real?.share_status;
  const payslipId = real?.payslip_id;
  const fileRef = useRef(null);
  const [correccionOpen, setCorreccionOpen] = useState(false);
  const [correccionNote, setCorreccionNote] = useState("");
  const refreshNomina = () => { qc.invalidateQueries({ queryKey: ["pren-sheet"] }); qc.invalidateQueries({ queryKey: ["rrhh-nominas"] }); };

  const validarMut = useMutation({
    mutationFn: async () => (await api.post(`/rrhh/nominas/${payslipId}/validar`)).data,
    onSuccess: () => { refreshNomina(); setBkOpen(false); toast.success(`Nómina compartida · ${row.name}`); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const correccionMut = useMutation({
    mutationFn: async () => (await api.post(`/rrhh/nominas/${payslipId}/correccion`, { note: correccionNote })).data,
    onSuccess: () => { refreshNomina(); setCorreccionOpen(false); setBkOpen(false); setCorreccionNote(""); toast.info(`Marcada para corrección · ${row.name}`); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const replaceMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return (await api.post(`/rrhh/nominas/${payslipId}/replace`, fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 })).data;
    },
    onSuccess: () => { refreshNomina(); toast.success("PDF reemplazado y reprocesado"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const onPickFile = (e) => { const f = e.target.files?.[0]; if (f) replaceMut.mutate(f); e.target.value = ""; };

  return (
    <>
    <TableRow data-testid={`pren-row-${row.user_id}`} className={row.reviewed ? "" : "bg-amber-50/40"}>
      <TableCell className="whitespace-nowrap font-medium">
        {row.name}
        {row.is_sales_team && <Badge variant="outline" className="ml-1.5 border-sky-200 bg-sky-50 text-[10px] text-sky-700">ventas</Badge>}
        {!row.reviewed && <span className="ml-1.5 text-[10px] text-amber-600" title={`Precargado: ${SRC_LABEL[row.prefill_source] || ""}`}>({SRC_LABEL[row.prefill_source] || "—"})</span>}
      </TableCell>
      <TableCell><Input className="h-8 w-28" value={form.nivel_profesional} onChange={(e) => set("nivel_profesional", e.target.value)} placeholder="Nivel/Grupo" data-testid={`pren-nivel-${row.user_id}`} /></TableCell>
      <TableCell><Input type="number" className="h-8 w-28 text-right" value={form.bruto_base} onChange={(e) => set("bruto_base", e.target.value)} data-testid={`pren-bruto-${row.user_id}`} /></TableCell>
      <TableCell className="text-right text-muted-foreground" data-testid={`pren-comisiones-${row.user_id}`}>{row.is_sales_team ? eur(row.comisiones) : "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <Input type="number" className="h-8 w-14 text-center" value={form.num_guardias} onChange={(e) => set("num_guardias", e.target.value)} data-testid={`pren-guardias-${row.user_id}`} />
          <span className="text-[11px] text-muted-foreground">×{rate}€</span>
        </div>
      </TableCell>
      <TableCell><Input type="number" className="h-8 w-24 text-right" value={form.plus_productividad} onChange={(e) => set("plus_productividad", e.target.value)} data-testid={`pren-plus-${row.user_id}`} /></TableCell>
      <TableCell className="text-right font-bold text-primary" data-testid={`pren-total-${row.user_id}`}>{eur(totalPrev)}</TableCell>
      <TableCell className="text-right" data-testid={`pren-real-${row.user_id}`}>
        {row.real && row.real.bruto != null ? (
          <div className="flex flex-col items-end gap-1">
            <span>{eur(row.real.bruto)}</span>
            <span data-testid={`pren-share-status-${row.user_id}`}><ShareBadge status={shareStatus} /></span>
          </div>
        ) : <span className="text-muted-foreground">—</span>}
      </TableCell>
      <TableCell className="text-right" data-testid={`pren-dif-${row.user_id}`}>
        {dif == null ? <span className="text-muted-foreground">—</span>
          : <button type="button" onClick={() => setBkOpen(true)} data-testid={`pren-dif-btn-${row.user_id}`}
              className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 underline-offset-2 hover:underline ${Math.abs(dif) < 0.5 ? "text-emerald-600" : "font-semibold text-rose-600"}`}
              title="Ver desglose de la diferencia">
              {dif > 0 ? "+" : ""}{eur(dif)}
              {Math.abs(dif) >= 0.5 && <ShieldAlert className="h-3.5 w-3.5" />}
            </button>}
      </TableCell>
      <TableCell>
        <Button size="sm" variant={dirty ? "default" : "outline"} className="h-8" disabled={!dirty || saveMut.isPending} onClick={() => saveMut.mutate()} data-testid={`pren-save-${row.user_id}`}>
          {saveMut.isPending ? "…" : dirty ? "Guardar" : <Check className="h-4 w-4" />}
        </Button>
      </TableCell>
    </TableRow>

    <Dialog open={bkOpen} onOpenChange={setBkOpen}>
      <DialogContent className="max-w-lg" data-testid={`pren-breakdown-${row.user_id}`}>
        <DialogHeader>
          <DialogTitle>Comprobación de nómina · {row.name}</DialogTitle>
          <DialogDescription>Comparación por concepto entre lo previsto por SOFI y la nómina real subida ({MESES[month - 1]} {year}).</DialogDescription>
        </DialogHeader>
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Concepto</TableHead>
              <TableHead className="text-right">Previsto</TableHead>
              <TableHead className="text-right">Real</TableHead>
              <TableHead className="text-right">Dif.</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {bkRows.map((b) => (
                <TableRow key={b.c} data-testid={`pren-bk-${row.user_id}`} className={Math.abs(b.dif) >= 0.5 ? "bg-rose-50/50" : ""}>
                  <TableCell className="font-medium">{b.c}</TableCell>
                  <TableCell className="text-right">{eur(b.prev)}</TableCell>
                  <TableCell className="text-right">{eur(b.real)}</TableCell>
                  <TableCell className={`text-right ${Math.abs(b.dif) < 0.5 ? "text-emerald-600" : "font-semibold text-rose-600"}`}>{b.dif > 0 ? "+" : ""}{eur(b.dif)}</TableCell>
                </TableRow>
              ))}
              <TableRow className="border-t-2 bg-muted/40 font-bold">
                <TableCell>Total</TableCell>
                <TableCell className="text-right">{eur(totalPrev)}</TableCell>
                <TableCell className="text-right">{eur(real?.bruto)}</TableCell>
                <TableCell className={`text-right ${dif != null && Math.abs(dif) < 0.5 ? "text-emerald-600" : "text-rose-600"}`}>{dif != null && dif > 0 ? "+" : ""}{eur(dif)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
        {real?.devengos?.length > 0 && (
          <div className="max-h-40 overflow-y-auto rounded-lg border p-2">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Devengos de la nómina real</p>
            <ul className="space-y-0.5 text-xs">
              {real.devengos.map((d, i) => (
                <li key={i} className="flex justify-between gap-3"><span className="text-muted-foreground">{d.concepto}</span><span className="tabular-nums">{eur(d.importe)}</span></li>
              ))}
            </ul>
          </div>
        )}
        <p className="flex items-start gap-1.5 text-[11px] text-muted-foreground">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Una diferencia en «Comisiones» suele indicar ventas fuera del periodo 16→15, cambios de estado o datos migrados. «Otros» son conceptos de la nómina real no contemplados en la prenómina (IRPF en especie, atrasos, etc.).
        </p>

        {payslipId && (
          <div className="space-y-3 rounded-lg border bg-muted/20 p-3" data-testid={`pren-actions-${row.user_id}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <FileText className="h-3.5 w-3.5" />Validación de la nómina
              </span>
              <ShareBadge status={shareStatus} />
            </div>
            {shareStatus === "compartida" ? (
              <p className="flex items-center gap-1.5 text-[11px] text-emerald-700">
                <Check className="h-3.5 w-3.5" />Compartida con {row.name}. Disponible para el empleado a partir del {disponibleDesde(year, month)}.
              </p>
            ) : shareStatus === "requiere_correccion" ? (
              <p className="flex items-start gap-1.5 text-[11px] text-rose-700">
                <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />Marcada para corrección. Oculta para el empleado hasta reemplazar el PDF.
              </p>
            ) : (
              <p className="flex items-start gap-1.5 text-[11px] text-amber-700">
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />Pendiente de validar. El empleado aún no puede verla.
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {shareStatus !== "compartida" && (
                <Button size="sm" className="h-8" onClick={() => validarMut.mutate()} disabled={validarMut.isPending} data-testid={`pren-validar-${row.user_id}`}>
                  <Share2 className="mr-1.5 h-3.5 w-3.5" />{validarMut.isPending ? "Compartiendo…" : "Validar y compartir"}
                </Button>
              )}
              {shareStatus !== "requiere_correccion" && (
                <Button size="sm" variant="outline" className="h-8 border-rose-200 text-rose-700 hover:bg-rose-50" onClick={() => setCorreccionOpen(true)} data-testid={`pren-correccion-${row.user_id}`}>
                  <AlertCircle className="mr-1.5 h-3.5 w-3.5" />Requiere corrección
                </Button>
              )}
              <Button size="sm" variant="outline" className="h-8" onClick={() => fileRef.current?.click()} disabled={replaceMut.isPending} data-testid={`pren-replace-${row.user_id}`}>
                <Upload className="mr-1.5 h-3.5 w-3.5" />{replaceMut.isPending ? "Reemplazando…" : "Reemplazar PDF"}
              </Button>
              <input ref={fileRef} type="file" accept="application/pdf,.pdf" className="hidden" onChange={onPickFile} data-testid={`pren-replace-input-${row.user_id}`} />
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>

    <Dialog open={correccionOpen} onOpenChange={setCorreccionOpen}>
      <DialogContent className="max-w-md" data-testid={`pren-correccion-dialog-${row.user_id}`}>
        <DialogHeader>
          <DialogTitle>Marcar para corrección · {row.name}</DialogTitle>
          <DialogDescription>La nómina quedará oculta para el empleado hasta que reemplaces el PDF. Puedes anotar el motivo.</DialogDescription>
        </DialogHeader>
        <Textarea rows={3} placeholder="Motivo de la corrección (opcional)…" value={correccionNote} onChange={(e) => setCorreccionNote(e.target.value)} data-testid={`pren-correccion-note-${row.user_id}`} />
        <DialogFooter>
          <Button variant="outline" onClick={() => setCorreccionOpen(false)}>Cancelar</Button>
          <Button className="bg-rose-600 hover:bg-rose-700" onClick={() => correccionMut.mutate()} disabled={correccionMut.isPending} data-testid={`pren-correccion-confirm-${row.user_id}`}>
            {correccionMut.isPending ? "Guardando…" : "Marcar para corrección"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

export default function PrenominaTab() {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [cfgOpen, setCfgOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);

  const shift = (delta) => {
    let m = month + delta, y = year;
    if (m < 1) { m = 12; y -= 1; } else if (m > 12) { m = 1; y += 1; }
    setMonth(m); setYear(y);
  };

  const q = useQuery({
    queryKey: ["pren-sheet", year, month],
    queryFn: async () => (await api.get("/prenomina/sheet", { params: { year, month } })).data,
  });
  const data = q.data;
  const t = data?.totals;

  return (
    <div className="space-y-4" data-testid="pren-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><ClipboardList className="h-6 w-6 text-primary" />Prenómina mensual</h1>
          <p className="text-sm text-muted-foreground">Prepara los datos para el gestor laboral y cótejalos con la nómina real. Se reporta el día 22.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(-1)} data-testid="pren-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[130px] text-center text-sm font-semibold" data-testid="pren-month-label">{MESES[month - 1]} {year}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => shift(1)} data-testid="pren-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setMembersOpen(true)} data-testid="pren-members-btn" title="Empleados en la prenómina"><Users className="h-4 w-4" /></Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setCfgOpen(true)} data-testid="pren-config-btn" title="Configuración"><Settings2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {q.isLoading ? <Skeleton className="h-80 w-full" /> : !data || data.rows.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-muted-foreground" data-testid="pren-empty">No hay empleados para mostrar.</CardContent></Card>
      ) : (
        <Card data-testid="pren-sheet">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Empleado</TableHead>
                    <TableHead>Nivel prof.</TableHead>
                    <TableHead className="text-right">Bruto base</TableHead>
                    <TableHead className="text-right">Comisiones</TableHead>
                    <TableHead>Guardias</TableHead>
                    <TableHead className="text-right">Plus prod.</TableHead>
                    <TableHead className="text-right">Total previsto</TableHead>
                    <TableHead className="text-right">Nómina real</TableHead>
                    <TableHead className="text-right">Dif.</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r) => <EmployeeRow key={r.user_id} row={r} year={year} month={month} />)}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 border-t bg-primary/5 px-4 py-3 text-sm">
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-muted-foreground">
                <span>Bruto base: <b className="text-foreground">{eur(t.bruto_base)}</b></span>
                <span>Comisiones: <b className="text-foreground">{eur(t.comisiones)}</b></span>
                <span>Guardias: <b className="text-foreground">{eur(t.guardias_importe)}</b></span>
                <span>Plus prod.: <b className="text-foreground">{eur(t.plus_productividad)}</b></span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-heading font-bold">Total previsto ({t.num_empleados} empleados)</span>
                <span className="font-heading text-lg font-bold text-primary" data-testid="pren-grand-total">{eur(t.total_previsto)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex items-start gap-2 rounded-lg border border-dashed p-3 text-xs text-muted-foreground">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>Total previsto = Bruto base + Comisiones (periodo 16→15, a mes vencido) + Guardias (nº × tarifa) + Plus de productividad. El bruto base y el nivel se precargan de la última nómina y se arrastran de mes a mes. La columna «Dif.» compara con la nómina real cuando esté subida.</span>
      </div>

      <ConfigDialog open={cfgOpen} onClose={() => setCfgOpen(false)} />
      <MembersDialog open={membersOpen} onClose={() => setMembersOpen(false)} />
    </div>
  );
}
